/**
 * ZoomSlider Component
 * 
 * A vertical slider for controlling zoom level with smooth drag interactions.
 * Designed to work with the useZoom hook.
 */

import React, { useRef, useState, useEffect, useCallback } from 'react';

// ============================================================================
// Constants
// ============================================================================

const CENTER_BUTTON_IMAGE_PATH = '/assets/ui/centre-button.svg';

// ============================================================================
// Types
// ============================================================================

interface ZoomSliderProps {
  zoom: number;
  minZoom: number;
  maxZoom: number;
  onZoomChange: (zoom: number) => void;
  playerPosition?: { q: number; r: number };
  onCenterChange?: (position: { q: number; r: number }) => void;
  style?: React.CSSProperties;
}

// ============================================================================
// Component
// ============================================================================

export function ZoomSlider({
  zoom,
  minZoom,
  maxZoom,
  onZoomChange,
  playerPosition,
  onCenterChange,
  style,
}: ZoomSliderProps): React.ReactElement {
  const [isDragging, setIsDragging] = useState(false);
  const [centerButtonImage, setCenterButtonImage] = useState<HTMLImageElement | null>(null);
  const sliderRef = useRef<HTMLDivElement>(null);
  const renderAnimationFrameRef = useRef<number | null>(null);
  
  // Load center button image
  useEffect(() => {
    const img = new Image();
    img.src = CENTER_BUTTON_IMAGE_PATH;
    img.onload = () => {
      setCenterButtonImage(img);
    };
    img.onerror = () => {
      console.warn('Failed to load center button image:', CENTER_BUTTON_IMAGE_PATH);
    };
  }, []);

  // Calculate slider position (0 = top = max zoom, 1 = bottom = min zoom)
  const sliderPosition = 1 - (zoom - minZoom) / (maxZoom - minZoom);

  // Handle slider drag start or click on track
  const handleSliderMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (!sliderRef.current) return;

    const slider = sliderRef.current;
    const rect = slider.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const percentage = Math.max(0, Math.min(1, y / rect.height));

    // Invert: top (0) = max zoom, bottom (1) = min zoom
    const newZoom = minZoom + (1 - percentage) * (maxZoom - minZoom);

    onZoomChange(newZoom);
    setIsDragging(true);
  };

  // Handle slider drag
  const handleSliderMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging || !sliderRef.current) return;

    const slider = sliderRef.current;
    const rect = slider.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const percentage = Math.max(0, Math.min(1, y / rect.height));

    // Invert: top (0) = max zoom, bottom (1) = min zoom
    const newZoom = minZoom + (1 - percentage) * (maxZoom - minZoom);

    // Use requestAnimationFrame for smooth slider updates
    if (renderAnimationFrameRef.current) {
      cancelAnimationFrame(renderAnimationFrameRef.current);
    }
    renderAnimationFrameRef.current = requestAnimationFrame(() => {
      onZoomChange(newZoom);
      renderAnimationFrameRef.current = null;
    });
  }, [isDragging, minZoom, maxZoom, onZoomChange]);

  // Handle slider drag end
  const handleSliderMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Set up global mouse events for slider dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleSliderMouseMove);
      document.addEventListener('mouseup', handleSliderMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleSliderMouseMove);
        document.removeEventListener('mouseup', handleSliderMouseUp);
      };
    }
  }, [isDragging, handleSliderMouseMove, handleSliderMouseUp]);

  const handleCenterClick = () => {
    if (playerPosition && onCenterChange) {
      onCenterChange(playerPosition);
    }
    // Set zoom to 50% of the zoom range (midpoint between minZoom and maxZoom)
    const zoomRange = maxZoom - minZoom;
    const targetZoom = minZoom + (zoomRange * 0.5); // 50% of the range
    onZoomChange(targetZoom);
  };

  return (
    <div
      style={{
        position: 'absolute',
        right: 15,
        bottom: 15,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 10,
        zIndex: 10,
        ...style,
      }}
    >
      {/* Center on Player Button */}
      {playerPosition && onCenterChange && (
        <button
          onClick={handleCenterClick}
          style={{
            width: 30,
            height: 30,
            background: 'rgba(0, 0, 0, 0.7)',
            border: '2px solid #666',
            borderRadius: '15px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            transition: 'background 0.2s ease',
            flexShrink: 0,
            overflow: 'hidden',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(0, 0, 0, 0.9)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(0, 0, 0, 0.7)';
          }}
          title="Center on player and zoom to 50%"
        >
          {centerButtonImage ? (
            <img
              src={centerButtonImage.src}
              alt="Center on player"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              }}
            />
          ) : (
            <span style={{ color: '#fff', fontSize: '16px' }}>🎯</span>
          )}
        </button>
      )}

      {/* Zoom Slider */}
      <div
        ref={sliderRef}
        style={{
          width: 30,
          height: 200,
          background: 'rgba(0, 0, 0, 0.7)',
          border: '2px solid #666',
          borderRadius: '15px',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 0',
          position: 'relative', // Ensure absolute children are contained
          overflow: 'hidden', // Prevent track from extending beyond container
        }}
        onMouseDown={handleSliderMouseDown}
      >
        {/* Zoom labels */}
        <span style={{ color: '#fff', fontSize: '10px', fontWeight: 'bold', zIndex: 2 }}>+</span>
        <span style={{ color: '#fff', fontSize: '10px', fontWeight: 'bold', zIndex: 2 }}>-</span>

        {/* Slider track - contained within the slider container */}
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: '20px',
            bottom: '20px',
            width: '4px',
            background: '#555',
            borderRadius: '2px',
            transform: 'translateX(-50%)',
            zIndex: 0, // Ensure track is behind handle and labels
          }}
        />

        {/* Slider handle */}
        <div
          style={{
            position: 'absolute',
            left: '50%',
            top: `${20 + sliderPosition * 160}px`,
            width: '20px',
            height: '20px',
            background: 'linear-gradient(135deg, #8b7355 0%, #6a5a45 100%)',
            border: '2px solid #5a4a35',
            borderRadius: '50%',
            transform: 'translate(-50%, -50%)',
            cursor: 'grab',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
            transition: isDragging ? 'none' : 'top 0.1s ease',
            zIndex: 1, // Ensure handle is above track
          }}
          onMouseDown={(e) => {
            e.stopPropagation();
            setIsDragging(true);
          }}
        />

      {/* Current zoom value */}
      <div
        style={{
          position: 'absolute',
          bottom: -25,
          left: '50%',
          transform: 'translateX(-50%)',
          color: '#fff',
          fontSize: '11px',
          fontWeight: 'bold',
          whiteSpace: 'nowrap',
          background: 'rgba(0, 0, 0, 0.8)',
          padding: '2px 6px',
          borderRadius: '4px',
        }}
      >
        {zoom.toFixed(1)}x
      </div>
      </div>
    </div>
  );
}

